import React, { useCallback, useEffect, useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import { type JSONDocNode } from '@atlaskit/editor-json-transformer';
import { fg } from '@atlaskit/platform-feature-flags';
import { RovoAgentIcon } from '@atlassian/conversation-assistant-ui-components';
import type {
	FocusInputRefType,
	Suggestion,
} from '@atlassian/generative-ai-modal/screens/UserInputCommandPalette';
import { UserInputCommandPalette } from '@atlassian/generative-ai-modal/screens/UserInputCommandPalette';
import { type EditorAgent } from '@atlassian/generative-ai-modal/utils/agents';

import { useAnalyticsFlowEventsQueue } from '../../analytics/analytics-flow/analyticsFlowUtils';
import {
	type EditorPluginAIConfigItemMarkdown,
	getTranslatedItemTitle,
	getTranslatedPromptLabel,
} from '../../config-items/config-items';
import { openBrowseAgentsModal } from '../../pm-plugins/rovo-agents/commands';
import { useAgents } from '../../pm-plugins/rovo-agents/useAgents';
import { createAgentConfigItem } from '../../prebuilt/config-item-agents/create-agent-config-item';
import { PreloadModalComponent } from '../../ui/components/BrowseRovoAgentsModal/BrowseRovoAgentsModal';
import type { AIExperienceMachineContext } from '../get-ai-experience-service';
import { messages } from '../messages';
import { useAIExperienceCommonDataContext } from '../useAIExperienceCommonData';
import { useCommandPaletteStateMachine } from '../useCommandPaletteStateMachine';
import { useOutsideClickHandler } from '../useOutsideClickHandler';
/**
 * This component is responsible for the logic and rendering of the
 * Command Palette component, it also handles the corresponding state machine.
 */
export function UserInputCommandPaletteWithLogic({
	context: experienceAppContext,
	initialPrompt,
}: {
	context: AIExperienceMachineContext;
	initialPrompt?: string;
}) {
	const aiExperienceCommonData = useAIExperienceCommonDataContext();
	const { appearance, editorPluginAIProvider, endExperience, sendToAIExperienceMachine, modalRef } =
		aiExperienceCommonData;

	const { formatMessage } = useIntl();
	const {
		configItem: startingConfig,
		baseGenerate,
		userInput: startingUserInput,
		userADFInput: startingUserADFInput,
		editCount,
		positions,
		editorView,
		latestPromptResponse,
		aiProvider,
	} = experienceAppContext;

	const [initialPromptTriggered, setInitialPromptTriggered] = React.useState(false);

	const { context: commandPaletteContext, send: sendToCommandPalette } =
		useCommandPaletteStateMachine({
			baseGenerate,
			startingConfig,
			startingPresetPromptLabel: getTranslatedPromptLabel(startingConfig, formatMessage),
			startingUserInput,
			startingUserADFInput,
		});

	const { textInput, adfInput, configItem, presetPromptLabel } = commandPaletteContext;

	useOutsideClickHandler({
		modalRef,
		userInput: commandPaletteContext.textInput,
		onCancel: () => aiExperienceCommonData.endExperience(),
		onDiscard: () => {
			sendToAIExperienceMachine({
				type: 'update context',
				context: {
					configItem: configItem,
					userInput: textInput || '',
					userADFInput: adfInput,
				},
			});
			sendToAIExperienceMachine({ type: 'enable discard' });
		},
	});

	const focusInputRef = React.useRef<FocusInputRefType>(undefined);

	const handleCancel = () => {
		const isInputEmpty = !commandPaletteContext.textInput
			? true
			: commandPaletteContext.textInput.trim().length === 0;
		if (isInputEmpty) {
			endExperience();
		} else {
			sendToAIExperienceMachine({
				type: 'update context',
				context: {
					configItem,
					userInput: textInput || '',
					userADFInput: adfInput,
				},
			});
			sendToAIExperienceMachine({
				type: 'cancel',
			});
		}
	};

	const kickoffAiExperience = useCallback(
		({
			configItem,
			userInput,
			userADFInput,
		}: {
			configItem: EditorPluginAIConfigItemMarkdown;
			userInput: string;
			userADFInput?: JSONDocNode;
		}) =>
			sendToAIExperienceMachine({
				type: 'kickoff with command palette input',
				context: {
					configItem,
					userInput,
					userADFInput,
				},
			}),
		[sendToAIExperienceMachine],
	);

	const onPartialPresetSelected = useCallback(
		(
			configItem: EditorPluginAIConfigItemMarkdown,
			{ presetPromptLabel }: { presetPromptLabel?: string } = {},
		) => {
			// If partial prompt is selected we want to update the command palette
			// And focus the text input
			sendToCommandPalette({
				type: 'onPartialPresetSelected',
				configItem,
				presetPromptLabel: presetPromptLabel ?? getTranslatedPromptLabel(configItem, formatMessage),
			});
			focusInputRef.current?.();
		},
		[sendToCommandPalette, formatMessage],
	);

	const analyticsFlow = useAnalyticsFlowEventsQueue({
		stepName: 'prompt',
		attributes: {
			experienceName: configItem.key,
			promptTitle: getTranslatedPromptLabel(configItem, formatMessage),
			// !TODO AIFOLLOWUP - Remove this
			// This attribute only made sense when we had two different entry points
			// 'custom' or 'generate'
			// This should likely change to respresent the actual prompt types:
			// 'full-prompt' = when there is no user input (e.g. 'Summarize this page')
			// 'partial-prompt' = when there is user input and a prompt (e.g. 'Brainstorm: "Titles for this page"')
			// 'generate' = there is only user input, and no preset prompt
			promptType: 'generate',
			usingCommandPalette: true,
			editCount,
		},
	});

	const getPromptTitle = (configItem: EditorPluginAIConfigItemMarkdown) => {
		if (configItem.agent?.creatorType !== 'SYSTEM') {
			return undefined;
		}
		return typeof configItem.promptLabel?.defaultMessage === 'string'
			? configItem.promptLabel?.defaultMessage
			: configItem.key;
	};

	const updateAnalytics = useCallback(
		(configItem: EditorPluginAIConfigItemMarkdown) => {
			/**
			 * Update the backendModel common attribute whenever the configItem changes
			 */
			analyticsFlow.updateCommonAttributes({
				backendModel: configItem?.getBackendModel?.(!!latestPromptResponse),
				agentId: configItem.agent?.id,
				agentName: configItem.agent?.creatorType === 'SYSTEM' ? configItem.agent?.name : undefined,
			});
			// update experiencename and title as it changes on selecting suggestion
			// If agent is selected, add agent id and agent name to the attributes
			analyticsFlow.addAttributes({
				experienceName: configItem.key,
				promptTitle: getPromptTitle(configItem),
			});
		},
		[analyticsFlow, latestPromptResponse],
	);

	const rovoAgentsPluginState = useAgents({ editorState: editorView.state });

	const updateTextInput = (textInputValue: string) => {
		sendToCommandPalette({
			type: 'onTextEntered',
			input: textInputValue,
		} as { type: 'onTextEntered'; input: string });
	};

	const updateADFInput = React.useCallback(
		(text: JSONDocNode) => {
			sendToCommandPalette({
				type: 'onADFEntered',
				adf: text,
			});
		},
		[sendToCommandPalette],
	);

	const onSubmit = useCallback(() => {
		kickoffAiExperience({
			configItem: commandPaletteContext.configItem,
			userInput: commandPaletteContext.textInput || '',
			userADFInput: commandPaletteContext.adfInput,
		});
	}, [
		kickoffAiExperience,
		commandPaletteContext.configItem,
		commandPaletteContext.textInput,
		commandPaletteContext.adfInput,
	]);

	const __getSuggestions = useMemo(() => {
		return configItem.selectionType === 'range'
			? editorPluginAIProvider.rangeConfig.getSuggestions
			: editorPluginAIProvider.emptyConfig.getSuggestions;
	}, [
		configItem.selectionType,
		editorPluginAIProvider.emptyConfig.getSuggestions,
		editorPluginAIProvider.rangeConfig.getSuggestions,
	]);

	/**
	 * This function is responsible for highlighting the entire document
	 * when particular prompts are selected.
	 *
	 * This only happens if the user has not already made a selection.
	 *
	 * Currently all prompts highlight the whole document except partial prompts like:
	 * - Brainstorm
	 * - Ask Search
	 */
	const handleHighlight = useCallback(
		({
			configItem,
			promptLabel,
		}: {
			configItem: EditorPluginAIConfigItemMarkdown;
			promptLabel?: string;
		}) => {
			// Remove all decorations if the prompt is 'Tell AI' or that it is not defined (brainstorm)
			if (configItem.promptLabel !== undefined || promptLabel === 'tell-ai') {
				const removeTransaction = editorView.state.tr;
				removeTransaction.setMeta('hideSelectionPreview', true);
				editorView.dispatch(removeTransaction);
				return;
			}

			// Highlight the entire document for all other prompts
			const setTransaction = editorView.state.tr;
			setTransaction.setMeta('showSelectionPreview', true);
			editorView.dispatch(setTransaction);
		},
		[editorView],
	);

	const onConverSationStarterSelected = useCallback(
		(conversationStarter: string) => {
			handleHighlight({ configItem });
			updateAnalytics(configItem);
			onPartialPresetSelected(configItem);
			kickoffAiExperience({
				userInput: conversationStarter,
				configItem,
			});
		},
		[handleHighlight, updateAnalytics, onPartialPresetSelected, kickoffAiExperience, configItem],
	);

	useEffect(() => {
		if (initialPrompt && !initialPromptTriggered) {
			onConverSationStarterSelected(initialPrompt);
			setInitialPromptTriggered(true);
		}
	}, [initialPrompt, onConverSationStarterSelected, initialPromptTriggered]);

	const getSuggestions = useCallback(
		(inputValue: string, agent?: EditorAgent): Suggestion[] => {
			// if not an agent, partial and no group, show nothing
			if (presetPromptLabel && !configItem.nestingConfig && !agent) {
				return [];
			}

			const tellAiSuggestion = !inputValue
				? []
				: [
						{
							value: agent ? 'tell-agent' : 'tell-ai',
							label: inputValue,
							onSelect: () => {
								handleHighlight({ configItem });
								updateAnalytics(configItem);
								onSubmit();
							},
							onSuggestionFocused: () => {
								handleHighlight({
									configItem,
									promptLabel: agent ? 'tell-agent' : 'tell-ai',
								});
							},
						},
					];

			/**
			 * If an agent is selected, we want to show the agents conversation starters
			 * as suggestions in the command palette with the 'Tell Agent' suggestion
			 * when there is user input
			 */
			if (agent) {
				const conversationStarterSuggestions = agent.conversationStarters
					.map((conversationStarter) => {
						const translatedMessage =
							typeof conversationStarter === 'string'
								? conversationStarter
								: formatMessage(conversationStarter);

						return {
							value: `agent:${agent.id}:${translatedMessage}`,
							label: translatedMessage,
							icon: <RovoAgentIcon label={translatedMessage} />,
							groupHeading: formatMessage(messages.agentsSuggestionGroupHeading),
							onSelect: () => {
								onConverSationStarterSelected(translatedMessage);
							},
							onSuggestionFocused: () => {
								handleHighlight({
									configItem,
								});
							},
						};
					})
					.filter((configItem) =>
						configItem.label.toLowerCase().includes((inputValue || '').toLowerCase()),
					);

				return [...conversationStarterSuggestions, ...tellAiSuggestion];
			}

			/**
			 * If an agent is not selected, we want to show the suggestions
			 */
			return (
				__getSuggestions({ inputValue })
					.filter((configItem) => {
						return configItem.isVisible({
							editorView,
							positions,
						});
					})
					.map<Suggestion>((configItem): Suggestion => {
						let icon = configItem.icon?.({ formatMessage, shownAt: 'suggestions' });

						// Show the icon for the parent config item if it exists
						if (
							configItem.nestingConfig &&
							configItem.nestingConfig.icon &&
							!presetPromptLabel &&
							!inputValue &&
							fg('platform_editor_ai_command_palate_improvement_fg')
						) {
							icon = configItem.nestingConfig.icon?.({ formatMessage, shownAt: 'suggestions' });
						}

						return {
							icon,
							label:
								configItem.nestingConfig &&
								presetPromptLabel &&
								fg('platform_editor_ai_command_palate_improvement_fg')
									? formatMessage(configItem.nestingConfig.shortTitle)
									: getTranslatedItemTitle(configItem, formatMessage),
							statusLozengeType: configItem.statusLozengeType,
							value: configItem.key,
							nestingConfig:
								configItem.nestingConfig && fg('platform_editor_ai_command_palate_improvement_fg')
									? {
											parentTitle: formatMessage(configItem.nestingConfig.parentTitle),
										}
									: undefined,
							onSuggestionFocused: () => {
								handleHighlight({ configItem });
							},
							onSelect: () => {
								// A config item nested parent will have matching config but no prompt label
								// will be selected
								if (
									configItem.nestingConfig &&
									!presetPromptLabel &&
									!inputValue &&
									fg('platform_editor_ai_command_palate_improvement_fg')
								) {
									onPartialPresetSelected(configItem, {
										presetPromptLabel: formatMessage(configItem.nestingConfig.parentTitle),
									});
								} else {
									handleHighlight({ configItem });
									updateAnalytics(configItem);

									// If full prompt is selected we want to kickoff the AI experience
									if (!configItem.promptLabel) {
										kickoffAiExperience({
											userInput: '',
											userADFInput: undefined,
											configItem,
										});
									} else {
										onPartialPresetSelected(configItem);
									}
								}
							},
						};
					})
					/**
					 * Only show the agents if the user hasn't already selected an agent
					 */
					.concat(
						!rovoAgentsPluginState?.enabled || !aiProvider.getChannelVariables || agent
							? []
							: rovoAgentsPluginState.agents.map((agent) => {
									const agentConfigItem = createAgentConfigItem(
										startingConfig.selectionType,
										agent,
									);

									return {
										value: `agent:${agent.id}`,
										label: agent.name,
										description: agent.description ?? '',
										icon: (
											<PreloadModalComponent>
												<RovoAgentIcon label={agent.name} />
											</PreloadModalComponent>
										),
										groupHeading: formatMessage(messages.agentsSuggestionGroupHeading),
										isAgent: true,
										onSelect: () => {
											handleHighlight({ configItem: agentConfigItem });
											updateAnalytics(agentConfigItem);
											onPartialPresetSelected(agentConfigItem);
										},
										onSuggestionFocused: () => {
											handleHighlight({
												configItem: agentConfigItem,
											});
										},
									};
								}),
					)
					// The browse agents option is only available when using the new <BrowseAgentsModal>
					// component as we need to use its state to send the selected agent to the command palette
					.concat(
						!rovoAgentsPluginState?.enabled ||
							!fg('platform_editor_ai_use_browse_rovo_agents_modal') ||
							!aiProvider.getChannelVariables ||
							agent
							? []
							: {
									value: `agent:browse-more-agents`,
									label: formatMessage(messages.agentsBrowseMore),
									icon: <RovoAgentIcon label="browse-more-agents" />,
									groupHeading: formatMessage(messages.agentsSuggestionGroupHeading),
									onSelect: () => {
										openBrowseAgentsModal(true)(editorView.state, editorView.dispatch);
									},
									onSuggestionFocused: () => {
										handleHighlight({
											configItem,
										});
									},
								},
					)
					.filter((suggestion) => {
						if (!suggestion) {
							return false;
						}

						// if is partial and group, ignore those that don't match the group
						if (
							presetPromptLabel &&
							configItem.nestingConfig &&
							suggestion.nestingConfig?.parentTitle !== presetPromptLabel
						) {
							return false;
						}

						// text filter

						const searchValues = [suggestion.label.toLowerCase()];

						if (suggestion?.isAgent) {
							searchValues.push('rovo', 'agent');
						}

						return searchValues.join(' ').includes((inputValue || '').toLowerCase());
					})
					.concat(tellAiSuggestion)
			);
		},
		[
			__getSuggestions,
			rovoAgentsPluginState?.enabled,
			rovoAgentsPluginState?.agents,
			formatMessage,
			handleHighlight,
			configItem,
			updateAnalytics,
			onSubmit,
			onPartialPresetSelected,
			kickoffAiExperience,
			editorView,
			positions,
			startingConfig,
			onConverSationStarterSelected,
			aiProvider.getChannelVariables,
			presetPromptLabel,
		],
	);

	return (
		<>
			<UserInputCommandPalette
				appearance={appearance}
				presetPromptLabel={presetPromptLabel}
				inputValue={textInput || ''}
				inputADFValue={adfInput}
				onInputUpdate={updateTextInput}
				onADFUpdate={updateADFInput}
				onCancel={handleCancel}
				onEscapeKeyPressed={handleCancel}
				onSubmit={onSubmit}
				onRefinementTagRemove={() => {
					// Set experienceName and promptTitle to default values
					analyticsFlow.addAttributes({
						experienceName: baseGenerate?.key,
						promptTitle: baseGenerate ? getTranslatedPromptLabel(baseGenerate, formatMessage) : '',
					});
					analyticsFlow.updateCommonAttributes({
						backendModel: baseGenerate?.getBackendModel?.(!!latestPromptResponse),
					});
					sendToCommandPalette({ type: 'onPartialPresetRemoved' });
					focusInputRef.current?.();
				}}
				placeholder={configItem.promptHint ? formatMessage(configItem.promptHint) : ''}
				getSuggestions={getSuggestions}
				focusInputRef={focusInputRef}
				PromptEditor={editorPluginAIProvider.PromptEditor}
				agent={configItem.agent}
			/>
		</>
	);
}
